import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls, MarkerType, Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import './DatabaseDiagram.css';

/**
 * Customer Portal API Flow Diagram
 * Shows the authentication and API flow for customer portal
 */

// Custom Node Components with Handles
const ClientNode = ({ data }) => (
  <div className="rf-node account-node">
    <Handle type="source" position={Position.Bottom} style={{ background: '#4a90e2' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const GatewayNode = ({ data }) => (
  <div className="rf-node region-node">
    <Handle type="target" position={Position.Top} style={{ background: '#4a90e2' }} />
    <Handle type="source" position={Position.Bottom} style={{ background: '#4a90e2' }} />
    <div className="node-header">{data.label}</div>
    <div className="node-subtitle">{data.subtitle}</div>
  </div>
);

const ServiceNode = ({ data }) => (
  <div className="rf-node database-node">
    <Handle type="target" position={Position.Top} style={{ background: '#4a90e2' }} />
    <Handle type="source" position={Position.Bottom} style={{ background: '#4a90e2' }} />
    <div className="node-header">{data.label}</div>
    {data.subtitle && <div className="node-subtitle">{data.subtitle}</div>}
  </div>
);

const ApiGroupNode = ({ data }) => (
  <div className="rf-node container-node">
    <Handle type="target" position={Position.Top} style={{ background: '#4a90e2' }} />
    <Handle type="source" position={Position.Bottom} style={{ background: '#4a90e2' }} />
    <div className="node-header">{data.label}</div>
    {data.subtitle && <div className="node-subtitle">{data.subtitle}</div>}
  </div>
);

const DataNode = ({ data }) => (
  <div className="rf-node storage-node">
    <Handle type="target" position={Position.Top} style={{ background: '#4a90e2' }} />
    <div className="node-header">{data.label}</div>
    {data.subtitle && <div className="node-subtitle">{data.subtitle}</div>}
  </div>
);

const nodeTypes = {
  client: ClientNode,
  gateway: GatewayNode,
  service: ServiceNode,
  apiGroup: ApiGroupNode,
  data: DataNode,
};

// Helper function to create professional edges
const createEdge = (id, source, target, sourceHandle, targetHandle) => ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  animated: true,
  style: { stroke: '#4a90e2', strokeWidth: 3 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#4a90e2', width: 20, height: 20 },
});

// Customer Portal API Flow Diagram
export const CustomerPortalFlowDiagram = () => {
  const { nodes, edges } = useMemo(() => {
    const nodes = [
      {
        id: 'customer',
        type: 'client',
        position: { x: 300, y: 0 },
        data: { label: 'Customer Portal', subtitle: 'React Application' },
      },
      {
        id: 'entra',
        type: 'gateway',
        position: { x: 300, y: 150 },
        data: { label: 'Entra External ID', subtitle: 'JWT Authentication' },
      },
      {
        id: 'apim',
        type: 'gateway',
        position: { x: 300, y: 300 },
        data: { label: 'API Management', subtitle: 'Gateway & Rate Limiting' },
      },
      {
        id: 'function-app',
        type: 'service',
        position: { x: 300, y: 450 },
        data: { label: 'Backend API', subtitle: 'Customer Portal APIs' },
      },
      {
        id: 'resources-api',
        type: 'apiGroup',
        position: { x: 0, y: 600 },
        data: { label: 'Resources API', subtitle: 'Bots, Knowledge Bases' },
      },
      {
        id: 'tenants-api',
        type: 'apiGroup',
        position: { x: 200, y: 600 },
        data: { label: 'Tenants API', subtitle: 'Multi-tenant Management' },
      },
      {
        id: 'users-api',
        type: 'apiGroup',
        position: { x: 400, y: 600 },
        data: { label: 'Users API', subtitle: 'User Management' },
      },
      {
        id: 'knowledge-api',
        type: 'apiGroup',
        position: { x: 600, y: 600 },
        data: { label: 'Knowledge API', subtitle: 'Knowledge Operations' },
      },
      {
        id: 'cosmos',
        type: 'data',
        position: { x: 100, y: 800 },
        data: { label: 'MongoDB', subtitle: 'Multi-tenant Data' },
      },
      {
        id: 'storage',
        type: 'data',
        position: { x: 500, y: 800 },
        data: { label: 'Storage Account', subtitle: 'Files & Assets' },
      },
    ];

    const edges = [
      createEdge('e1', 'customer', 'entra', 'bottom', 'top'),
      createEdge('e2', 'entra', 'apim', 'bottom', 'top'),
      createEdge('e3', 'apim', 'function-app', 'bottom', 'top'),
      createEdge('e4', 'function-app', 'resources-api', 'bottom', 'top'),
      createEdge('e5', 'function-app', 'tenants-api', 'bottom', 'top'),
      createEdge('e6', 'function-app', 'users-api', 'bottom', 'top'),
      createEdge('e7', 'function-app', 'knowledge-api', 'bottom', 'top'),
      createEdge('e8', 'resources-api', 'cosmos', 'bottom', 'top'),
      createEdge('e9', 'tenants-api', 'cosmos', 'bottom', 'top'),
      createEdge('e10', 'users-api', 'cosmos', 'bottom', 'top'),
      createEdge('e11', 'knowledge-api', 'storage', 'bottom', 'top'),
      createEdge('e12', 'knowledge-api', 'cosmos', 'bottom', 'top'),
    ];

    return { nodes, edges };
  }, []);

  return (
    <div className="database-diagram rf-container" style={{ height: '600px' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.4}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ animated: true, style: { stroke: '#4a90e2', strokeWidth: 3 } }}
      >
        <Background color="#f0f0f0" gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};
